import { NVD_ENDPOINT, defaultSleep, type RollingLimiter } from "./fetch.js";

/**
 * Route NVD page requests through the shared Worker cache.
 *
 * The client keeps its per-day fetch, resume and merge logic; only the
 * transport changes. A Worker hit returns immediately from R2. A Worker miss
 * fetches NVD once and caches it for everyone. If the Worker is missing, slow,
 * rate-limited or broken, the request falls back to direct NVD, paced by the
 * caller's limiter, so the station keeps working without the backend.
 */
export interface PoolProxyOptions {
  /** Worker origin, e.g. https://youarevulnerable-audio.<sub>.workers.dev */
  origin: string;
  fetchImpl?: typeof fetch;
  /** Used only for direct-NVD fallbacks. */
  limiter?: RollingLimiter;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
}

const FALLBACK_STATUSES = new Set([404, 429]);

function shouldFallBack(response: Response): boolean {
  return FALLBACK_STATUSES.has(response.status) || response.status >= 500;
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export function createProxiedFetch(options: PoolProxyOptions): typeof fetch {
  const base = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const origin = options.origin.replace(/\/$/, "");
  const sleep = options.sleep ?? defaultSleep;

  return (async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = requestUrl(input);
    if (!url.startsWith(NVD_ENDPOINT)) return base(input, init);

    const parsed = new URL(url);
    const proxyUrl = `${origin}/nvd${parsed.pathname}${parsed.search}`;
    try {
      const response = await base(proxyUrl, init);
      if (response.ok || !shouldFallBack(response)) return response;
    } catch (error) {
      if (init?.signal?.aborted) throw error;
    }

    if (init?.signal?.aborted) throw new DOMException("aborted", "AbortError");
    if (options.limiter) {
      const wait = options.limiter.delayUntilNext();
      if (wait > 0) await sleep(wait, init?.signal ?? new AbortController().signal);
      options.limiter.record();
    }
    return base(input, init);
  }) as typeof fetch;
}
